import React, { createContext, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useRolePermissions from '../Hooks/useRolePermissions';
import { getPathModuleAccessFlags } from '../utils/rolePermissions';

const defaultAccess = {
  moduleKey: null,
  canView: true,
  canAdd: true,
  canEdit: true,
  isViewOnly: false,
  restrictMutations: false,
};

const AdminModuleAccessContext = createContext(defaultAccess);

/**
 * Provides current-route module permission flags to admin UI
 * (Add / Edit / view-only enforcement).
 */
export function AdminModuleAccessProvider({ children }) {
  const { pathname } = useLocation();
  const { activeRole, isFullAccess } = useRolePermissions();

  const value = useMemo(() => {
    if (isFullAccess) {
      return {
        moduleKey: null,
        canView: true,
        canAdd: true,
        canEdit: true,
        isViewOnly: false,
        restrictMutations: false,
      };
    }
    const flags = getPathModuleAccessFlags(pathname, activeRole);
    // Lock forms / mutating CTAs whenever Edit is not granted (view-only or view+add without edit)
    const restrictMutations = Boolean(flags.canView && !flags.canEdit);
    return { ...flags, restrictMutations };
  }, [pathname, activeRole, isFullAccess]);

  return (
    <AdminModuleAccessContext.Provider value={value}>
      {children}
    </AdminModuleAccessContext.Provider>
  );
}

export function useAdminModuleAccess() {
  return useContext(AdminModuleAccessContext);
}

export default AdminModuleAccessContext;
